import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  IoClose,
  IoMenuOutline,
  IoPersonCircleOutline,
  IoSwapHorizontalOutline,
} from "react-icons/io5";
import "./Navbar.css";
import { jwtDecode } from "jwt-decode";
import ProfileModal from "./Profile/ProfileModal";
import { fetchProfileMe } from "./Profile/avatarApi";
import { useAudience } from "../context/AudienceContext";
// import { CartNavButton } from "./CartNavButton";

const NAV_ITEMS: ReadonlyArray<{ to: string; label: string; end?: boolean }> = [
  { to: "/", label: "Home", end: true },
  { to: "/how-it-works", label: "How it works" },
  { to: "/research", label: "Research" },
  { to: "/shopping", label: "Shop" },
  { to: "/contact", label: "Contact" },
];

const DESKTOP_QUERY = "(min-width: 1024px)";

export function Navbar() {
  const { audience, clearAudience } = useAudience();
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [providerDashboard, setProviderDashboard] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [initials, setInitials] = useState<string>("");
  const [displayName, setDisplayName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [profileOpen, setProfileOpen] = useState(false);

  const menuBtnRef = useRef<HTMLButtonElement | null>(null);
  const menuPanelRef = useRef<HTMLDivElement | null>(null);
  const firstRowRef = useRef<HTMLAnchorElement | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    const idToken = localStorage.getItem("idToken");
    if (!idToken) return;

    try {
      const decoded: any = jwtDecode(idToken);
      const groups = decoded["cognito:groups"] || [];
      if (groups.includes("provider") || groups.includes("Provider")) {
        setProviderDashboard(true);
      }
      setLoggedIn(true);

      const picture = decoded?.picture || decoded?.avatar_url || null;
      const name: string =
        [decoded?.given_name, decoded?.family_name].filter(Boolean).join(" ") ||
        decoded?.email ||
        "";
      const emailFromToken: string = decoded?.email || "";

      setAvatarUrl(picture || null);
      setDisplayName(name);
      setEmail(emailFromToken);
      if (name) {
        const parts = name.trim().split(/\s+/);
        const first = parts[0]?.[0] || "";
        const second = parts.length > 1 && parts[1] ? parts[1][0] || "" : "";
        setInitials((first + second).toUpperCase());
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!loggedIn) return;
    let alive = true;
    (async () => {
      try {
        const me = await fetchProfileMe();
        if (!alive || !me) return;
        if (me.avatarUrl) setAvatarUrl(me.avatarUrl);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
  }, [loggedIn]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  // Close the mobile panel if the viewport grows into the desktop layout.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: MediaQueryListEvent) => {
      if (e.matches) closeMenu();
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [closeMenu]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (
        menuOpen &&
        t &&
        !menuPanelRef.current?.contains(t) &&
        !menuBtnRef.current?.contains(t)
      ) {
        closeMenu();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [menuOpen, closeMenu]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeMenu]);

  useEffect(() => {
    if (menuOpen) {
      wasOpenRef.current = true;
      firstRowRef.current?.focus();
    } else if (wasOpenRef.current) {
      wasOpenRef.current = false;
      menuBtnRef.current?.focus();
    }
  }, [menuOpen]);

  // Freeze the page behind the full-screen menu so it does not scroll underneath.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setMenuOpen((v) => !v);
    }
  };

  const onMenuLinkClick = () => closeMenu();

  const handleSignOut = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("accessToken");
    window.location.href = "/login";
  };

  const onAvatarClick = () => {
    if (!loggedIn) {
      window.location.href = "/login";
      return;
    }
    closeMenu();
    setProfileOpen(true);
  };

  const changePerspective = () => {
    clearAudience();
    closeMenu();
    navigate("/");
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive ? "nav-link is-active" : "nav-link";

  return (
    <header className="navbar">
      <div className="navbar-inner">
        <Link to="/" className="navbar-logo" aria-label="Go to Home">
          <img
            src="/mrehabIcon.png"
            alt="mRehab Logo"
            width={36}
            height={36}
            className="navbar-logo-img"
          />
          <span className="navbar-logo-text">mRehab</span>
        </Link>

        <nav className="navbar-nav" aria-label="Main navigation">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={navLinkClass}
              onClick={closeMenu}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="navbar-actions">
          {/* Hide: Price and Cart */}
          {/* <CartNavButton /> */}

          {/* Hide: Sign in and Create account */}
          {/* {!loggedIn ? (
            <div className="navbar-auth">
              <Link to="/login" className="ui-btn ui-btn--ghost ui-btn--sm">
                Sign in
              </Link>
              <Link to="/signup" className="ui-btn ui-btn--sm">
                Create account
              </Link>
            </div>
          ) : ( */}
          {loggedIn && (
            <button
              type="button"
              className="navbar-avatar-btn"
              aria-label="Account menu"
              onClick={onAvatarClick}
            >
              <span className="ui-avatar" aria-hidden="true">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" />
                ) : initials ? (
                  initials
                ) : (
                  <IoPersonCircleOutline size={24} />
                )}
              </span>
            </button>
          )}
          {/* )} */}

          <button
            ref={menuBtnRef}
            type="button"
            className="ui-icon-btn navbar-menu-btn"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            aria-controls="main-menu"
            onClick={() => setMenuOpen((v) => !v)}
            onKeyDown={onMenuKeyDown}
          >
            {menuOpen ? (
              <IoClose size={24} aria-hidden="true" />
            ) : (
              <IoMenuOutline size={24} aria-hidden="true" />
            )}
          </button>
        </div>

        {/* Portalled to <body>: the navbar's backdrop-filter would otherwise
            trap this fixed overlay inside the header. */}
        {createPortal(
        <div
          id="main-menu"
          ref={menuPanelRef}
          aria-label="Menu"
          aria-hidden={!menuOpen}
          className={`navbar-panel ${menuOpen ? "is-open" : ""}`}
          onClick={(e) => {
            // The sheet fills the screen, so a tap on its empty area closes it.
            if (e.target === e.currentTarget) closeMenu();
          }}
        >
          <ul className="ui-list navbar-panel-list">
            {NAV_ITEMS.map((item, index) => (
              <li
                key={item.to}
                style={{ "--i": index } as React.CSSProperties}
              >
                <NavLink
                  ref={index === 0 ? firstRowRef : undefined}
                  to={item.to}
                  end={item.end}
                  className="ui-list-row navbar-panel-link"
                  onClick={onMenuLinkClick}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  <span className="ui-list-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
            {/* Hide: Price and Cart */}
            {/* <li>
              <CartNavButton
                variant="dropdown"
                className="navbar-panel-link"
                tabIndex={menuOpen ? 0 : -1}
              />
            </li> */}
          </ul>

          {audience !== null && (
            <>
              <button
                type="button"
                className="ui-list-row navbar-panel-link navbar-panel-secondary"
                onClick={changePerspective}
                tabIndex={menuOpen ? 0 : -1}
              >
                <IoSwapHorizontalOutline size={20} aria-hidden="true" />
                <span className="ui-list-label">Change perspective</span>
              </button>
            </>
          )}

          {/* Hide: Sign in and Create account */}
          {/* {!loggedIn ? (
            <>
              <hr className="ui-divider" />
              <div className="navbar-panel-auth" role="group" aria-label="Account">
                <Link
                  to="/login"
                  className="ui-btn ui-btn--secondary ui-btn--full"
                  onClick={onMenuLinkClick}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="ui-btn ui-btn--full"
                  onClick={onMenuLinkClick}
                  tabIndex={menuOpen ? 0 : -1}
                >
                  Create account
                </Link>
              </div>
            </>
          ) : null} */}
        </div>,
        document.body
        )}
      </div>

      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        displayName={displayName}
        email={email}
        initials={initials}
        avatarUrl={avatarUrl}
        isProvider={providerDashboard}
        onAvatarUpdated={(nextUrl) => setAvatarUrl(nextUrl)}
        onSignOut={handleSignOut}
      />
    </header>
  );
}
